/**
 * Is this error the BROWSER's IndexedDB failing, rather than our code?
 *
 * IndexedDB fails for reasons that belong to the device: Safari drops its
 * connection to the IndexedDB server process after the tab is backgrounded
 * ("Connection to Indexed Database server lost"), Chrome cannot open a
 * corrupt or locked LevelDB ("Internal error opening backing store", "Unable
 * to open database file on disk"), the disk is full ("FILE_ERROR_NO_SPACE",
 * QuotaExceededError), a large value's blob file is missing ("Failed to read
 * large IndexedDB value"), or there is no IndexedDB at all (Safari with
 * "Block all cookies", some embedded webviews → Dexie's MissingAPIError).
 * WebKit also reports a transaction its server already lost as "Attempt to get
 * records … without an in-progress transaction" — only WebKit ever sends it
 * (measured on Sentry, 2026-10-16), so it is the server, not a transaction we
 * held open across an await.
 *
 * Errors arrive in three wrappings, and all three are checked:
 *   - raw `DOMException` (`name` is the IDB error name);
 *   - Dexie's errors (`DatabaseClosedError`, `AbortError`, …) with the
 *     original on `.inner`;
 *   - lib0 / y-indexeddb's `new Error(domException)` — name `Error`, the IDB
 *     name only in the message ("UnknownError: …").
 *
 * Deliberately NOT matched, because they ARE our bugs: a DataError for a key
 * path that yields no value, a DataCloneError for an uncloneable value (only
 * the "out of memory" one is the device's), NotFoundError for a store that
 * does not exist, ConstraintError, a bare DatabaseClosedError (our own
 * `db.close()`), and a QuotaExceededError with no IndexedDB context
 * (`localStorage` is full). A plain fetch `AbortError` is not matched
 * either — only Dexie's bare "Transaction aborted".
 */

/** Names that only ever describe the storage environment. */
const ENVIRONMENT_NAMES = new Set(['MissingAPIError']);
/**
 * QuotaExceededError is also what `localStorage.setItem` throws, so the name
 * alone does not say IndexedDB: it counts only inside a wrapper (Dexie keeps
 * the IDB error on `.inner`) or with an IDB-only message (see `matchesOne`).
 */
const QUOTA_NAME = 'QuotaExceededError';

/**
 * Dexie's wrappers for a database that is closed or would not open. They are
 * the device's — EXCEPT when what they wrap is ours: Dexie also wraps a schema
 * it cannot apply (UpgradeError "Not yet support for changing primary key",
 * FLASHKA-VITE-1EQ), a downgrade (VersionError), and any exception thrown by a
 * `.upgrade()` callback. Judging the wrapper by its name alone sent every one
 * of those to memory storage with no report.
 *
 * A BARE DatabaseClosedError — no inner, Dexie's default "Database has been
 * closed" or "db.open() was cancelled" — is what OUR `db.close()` produces
 * (sign-out, in-tab account switch); the browser's failures always arrive
 * with a cause. It is not the device's, so it is not matched (see
 * `wrapperHasDeviceCause`).
 */
const OPEN_WRAPPER_NAMES = new Set(['DatabaseClosedError', 'OpenFailedError']);
const CLOSED_NAME = 'DatabaseClosedError';
/** Dexie's `${inner.name} ${inner.message}` — a cause, even when Sentry
 *  serialised the wrapper without its `.inner`. */
const CAUSE_PREFIX = /^[A-Z]\w*Error\b/;
/** Causes that are a bug or a schema of ours, never the device. Matched by
 *  name (an `.inner` Dexie kept) or as the prefix Dexie gives the wrapper's
 *  message (`${inner.name} ${inner.message}` — what Sentry serialises). */
const OWN_CAUSE_NAMES =
  'UpgradeError|SchemaError|InvalidTableError|VersionError|TypeError|ReferenceError|SyntaxError|RangeError';
const OWN_CAUSE_NAME = new RegExp(`^(${OWN_CAUSE_NAMES})$`);
const OWN_CAUSE_PREFIX = new RegExp(`^(${OWN_CAUSE_NAMES})\\b`);
/** Open failures Dexie can also raise unwrapped. */
const UNWRAPPED_OPEN_FAILURE = /^(UpgradeError|VersionError)$/;

/** Phrases only IndexedDB uses — they decide on their own. */
const IDB_ONLY_MESSAGE =
  /Indexed ?Database server|IndexedDB|backing store|Unable to open database file|without an in-progress transaction|Failed to (write blobs|delete record from object store)|FILE_ERROR_NO_SPACE|Transaction timed out due to inactivity/i;

/**
 * Phrases too generic to decide alone — "Internal error" is also what a
 * server's 500 says, and that IS ours. They count only on an error that
 * already looks like IndexedDB's: an IDB DOMException name, or lib0's
 * `new Error(domException)` whose message starts with that name.
 */
const GENERIC_MESSAGE =
  /full disk|Database has been closed|connection (was closed|is closing)|Internal error/i;
const IDB_ERROR_NAME =
  /^(UnknownError|AbortError|InvalidStateError|TimeoutError|DataError)$/;
const IDB_NAME_PREFIX =
  /^(UnknownError|AbortError|InvalidStateError|TimeoutError|DataError)\b/;

type ErrorLike = { name?: unknown; message?: unknown; inner?: unknown; cause?: unknown };

function asErrorLike(value: unknown): ErrorLike | null {
  return value !== null && typeof value === 'object' ? (value as ErrorLike) : null;
}

/** Does this Dexie closed/open wrapper carry a cause — i.e. not the bare
 *  DatabaseClosedError our own `db.close()` raises (or one wrapping it)? */
function wrapperHasDeviceCause(err: ErrorLike, depth = 0): boolean {
  const name = typeof err.name === 'string' ? err.name : '';
  if (name !== CLOSED_NAME) return true;
  const inner = asErrorLike(err.inner) ?? asErrorLike(err.cause);
  if (inner) {
    if (inner.name !== CLOSED_NAME) return true;
    return depth < 4 && wrapperHasDeviceCause(inner, depth + 1);
  }
  const message = typeof err.message === 'string' ? err.message : '';
  return CAUSE_PREFIX.test(message) && !message.startsWith(CLOSED_NAME);
}

function matchesOne(err: ErrorLike, wrapped: boolean): boolean {
  const name = typeof err.name === 'string' ? err.name : '';
  const message = typeof err.message === 'string' ? err.message : '';
  if (ENVIRONMENT_NAMES.has(name)) return true;
  if (name === QUOTA_NAME && wrapped) return true;
  // The cause decides (see OPEN_WRAPPER_NAMES); `isIndexedDbEnvironmentError`
  // has already ruled out a cause of ours, so what remains is the device's.
  if (OPEN_WRAPPER_NAMES.has(name) && wrapperHasDeviceCause(err)) return true;
  // Dexie's abort of a transaction the browser killed without an error.
  if (name === 'AbortError' && message === 'Transaction aborted') return true;
  if (name === 'DataCloneError') return /out of memory/i.test(message);
  if (IDB_ONLY_MESSAGE.test(message)) return true;
  return (
    GENERIC_MESSAGE.test(message) &&
    (IDB_ERROR_NAME.test(name) || IDB_NAME_PREFIX.test(message))
  );
}

/**
 * lib0's cursor helper (`iterateOnRequest`, behind y-indexeddb's `getLastKey`)
 * rejects with the request's raw `error` EVENT rather than its error
 * (FLASHKA-VITE-1R1: "Event `Event` (type=error) captured as promise
 * rejection", target `[object IDBRequest]`). The failure itself is the
 * request's `.error`, and it is judged like any other — so a request that
 * failed for a reason of ours (NotFoundError, ConstraintError) is still kept.
 */
const IDB_REQUEST_TAG = /^\[object IDB(OpenDB)?Request\]$/;

function idbRequestErrorOf(value: unknown): unknown {
  if (typeof Event === 'undefined' || !(value instanceof Event)) return undefined;
  if (value.type !== 'error' || !value.target) return undefined;
  if (!IDB_REQUEST_TAG.test(Object.prototype.toString.call(value.target)))
    return undefined;
  return (value.target as { error?: unknown }).error;
}

/**
 * A Dexie open/closed failure whose cause is OURS — a schema Dexie cannot
 * apply, a downgrade, or an exception thrown by an upgrade callback. The
 * database will not open on this device either way, so the session still
 * degrades to memory; but this one is reported, because a migration of ours
 * is what has to change.
 */
export function isOwnIndexedDbOpenFailure(err: unknown): boolean {
  const top = asErrorLike(err);
  if (!top) return false;
  const topName = typeof top.name === 'string' ? top.name : '';
  // Only an OPEN failure: a bare TypeError from our own code is a bug to
  // report, not a reason to give up the database.
  if (!OPEN_WRAPPER_NAMES.has(topName) && !UNWRAPPED_OPEN_FAILURE.test(topName))
    return false;
  return hasOwnCause(err);
}

/** Does the error, or anything it wraps, name a cause of ours? */
function hasOwnCause(err: unknown): boolean {
  let current = asErrorLike(err);
  for (let depth = 0; current && depth < 4; depth++) {
    const name = typeof current.name === 'string' ? current.name : '';
    const message = typeof current.message === 'string' ? current.message : '';
    if (OWN_CAUSE_NAME.test(name)) return true;
    if (OPEN_WRAPPER_NAMES.has(name) && OWN_CAUSE_PREFIX.test(message)) return true;
    current = asErrorLike(current.inner) ?? asErrorLike(current.cause);
  }
  return false;
}

export function isIndexedDbEnvironmentError(err: unknown): boolean {
  const requestError = idbRequestErrorOf(err);
  if (requestError !== undefined) return isIndexedDbEnvironmentError(requestError);
  // A wrapper around a cause of ours is ours, whatever the wrapper says.
  if (hasOwnCause(err)) return false;
  let current = asErrorLike(err);
  // Dexie nests at most two levels (DatabaseClosedError → AbortError →
  // UnknownError); the bound only guards a cyclic `cause`.
  for (let depth = 0; current && depth < 4; depth++) {
    if (matchesOne(current, depth > 0)) return true;
    current = asErrorLike(current.inner) ?? asErrorLike(current.cause);
  }
  return false;
}
